import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";

import { startPlanPrompt, type ProposalJob } from "../../api/proposal";
import { useIdentity } from "../../auth/useIdentity";

/** Owner 2026-11-01: about five corrections a plan, then type it again. */
const MAX_CORRECTIONS = 5;

/** Owner 2026-11-02: examples say how the price stands, except where no word of GST may show. */
export function promptExamples(speak: boolean): string[] {
  const inc = speak ? " including GST" : "";
  const exc = speak ? " excluding GST" : "";
  return [
    `50 Mbps, 30 days, ₹500 a month${inc}`,
    `150 Mbps for 30, 90, 180 and 365 days, ₹600 a month${inc}, 1.25% off per month, 365 days at 15% off`,
    `100 Mbps for 30 and 90 days, ₹799 and ₹2,299${exc}, 2 logins`,
  ];
}

/** Words added to what was typed. An operator with no GST on its screens states one final price. */
export const FINAL_PRICE = "The prices are final and include tax.";
export const PRICE_PLUS_TAX = "Tax is extra on these prices.";

/**
 * Plans › Create plan from prompt: new plans from typed words (owner 2026-11-01). The model reads
 * values, the software calculates, and the same preview as a document follows.
 * Only Apply creates anything; an existing plan is never changed from here.
 */
export function PromptPlans({ ns, effectiveFrom, speak, job, onStarted }: {
  ns: string;
  effectiveFrom: string;
  speak: boolean;
  job: ProposalJob | undefined;
  onStarted: (j: ProposalJob) => void;
}) {
  const identity = useIdentity();
  const [text, setText] = useState("");
  const [sent, setSent] = useState("");
  const [correction, setCorrection] = useState("");
  const [corrections, setCorrections] = useState(0);
  const go = useMutation<ProposalJob, Error, string>({
    mutationFn: (typed) => startPlanPrompt(identity, ns, speak ? typed : `${typed}\n${FINAL_PRICE}`, effectiveFrom || undefined),
    onSuccess: (j, typed) => {
      setSent(typed);
      onStarted(j);
    },
  });
  const mine = Boolean(sent) && job !== undefined;
  const asksTax = mine && (job?.problems ?? []).includes("price_includes_tax");
  const send = (more: string) => {
    setCorrections((n) => n + 1);
    setCorrection("");
    go.mutate(`${sent}\n${more}`);
  };
  const running = go.isPending || job?.status === "running";

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Type your new plans
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        New plans only. The AI reads what you type, the software calculates each price and shows how, and nothing is
        created until you approve it.
      </Typography>
      <TextField fullWidth multiline minRows={2} size="small" label="Describe the plans" value={text}
        slotProps={{ htmlInput: { maxLength: 2000 } }} onChange={(e) => setText(e.target.value)} />
      <Box sx={{ mt: 1, mb: 1 }}>
        {!effectiveFrom && (
          <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>Please choose the effective date before any prompt.</Typography>
        )}
        {promptExamples(speak).map((x) => (
          <Button key={x} size="small" sx={{ textTransform: "none", textAlign: "left" }} onClick={() => setText(x)}>{x}</Button>
        ))}
      </Box>
      <Button variant="contained" disabled={!text.trim() || !ns || !effectiveFrom || running}
        onClick={() => { setCorrections(0); go.mutate(text.trim()); }}>
        {go.isPending ? "Sending…" : "Propose from what I typed"}
      </Button>
      {go.isError && <Alert severity="error" sx={{ mt: 1 }}>{go.error.message}</Alert>}
      {asksTax && speak && !running && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Do these prices include GST?{" "}
          <Button size="small" onClick={() => send("The prices include GST.")}>Include GST</Button>
          <Button size="small" onClick={() => send(PRICE_PLUS_TAX)}>GST is extra</Button>
        </Alert>
      )}
      {mine && !running && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <TextField fullWidth size="small" label="Correct something in plain words" value={correction}
            disabled={corrections >= MAX_CORRECTIONS} slotProps={{ htmlInput: { maxLength: 500 } }}
            helperText={corrections >= MAX_CORRECTIONS ? "That is the limit; type the plans again above." : "This changes only this preview."}
            onChange={(e) => setCorrection(e.target.value)} />
          <Button variant="outlined" sx={{ height: 40 }} disabled={!correction.trim() || !effectiveFrom || corrections >= MAX_CORRECTIONS}
            onClick={() => send(correction.trim())}>Correct</Button>
        </Stack>
      )}
    </Paper>
  );
}
